<template>
  <div class="st-page">
    <Navigation />
    <div class="container" style="padding-top:20px;max-width:1300px;">
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="st-page-title"><i class="mdi mdi-credit-card-check me-2"></i>Payments</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="btn st-btn-outline btn-sm" @click="showBalance = !showBalance; if(showBalance && !lastBalance) loadLastBalance();">💰 Balance</button>
          <button class="btn st-btn-outline btn-sm" @click="exportCsv" :disabled="exporting">📥 CSV</button>
          <button class="btn st-btn-outline btn-sm" @click="exportPdf" :disabled="exporting">📄 PDF</button>
          <button class="btn st-btn-primary btn-sm" @click="loadAll">🔄</button>
        </div>
      </div>

      <!-- Balance (collapsible) -->
      <div v-if="showBalance" class="st-section mb-3" style="padding:14px;">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span style="font-size:13px;font-weight:600;color:#4FC3F7;">💰 Account Balance</span>
          <div class="d-flex gap-2">
            <button class="btn st-btn-outline btn-sm" style="padding:4px 12px;font-size:11px;" @click="checkBalance" :disabled="balanceLoading">
              <span v-if="balanceLoading" class="spinner-border spinner-border-sm me-1"></span>🔄 Refresh
            </button>
            <button class="btn" style="color:#4A6A8A;font-size:16px;padding:0 4px;" @click="showBalance=false">✕</button>
          </div>
        </div>
        <div v-if="lastBalance && lastBalance.accounts" style="font-size:13px;">
          <div style="font-size:22px;font-weight:800;color:#10B981;margin-bottom:6px;">KSh {{ Number(lastBalance.total || 0).toLocaleString(undefined, {minimumFractionDigits:2}) }}</div>
          <div v-for="a in lastBalance.accounts" :key="a.account" style="display:flex;gap:10px;font-size:12px;padding:2px 0;color:#8BA4C4;">
            <span style="min-width:140px;">{{ a.account }}:</span>
            <strong style="color:#E8EDF4;">{{ a.currency }} {{ Number(a.available).toLocaleString(undefined, {minimumFractionDigits:2}) }}</strong>
          </div>
          <div style="margin-top:6px;font-size:10px;color:#4A6A8A;">Last updated: {{ lastBalance.timestamp }}</div>
        </div>
        <div v-else-if="balanceResult" style="font-size:12px;color:#8BA4C4;">{{ balanceResult }}</div>
        <div v-else style="font-size:12px;color:#4A6A8A;">Click 🔄 Refresh to fetch balance from Safaricom.</div>
      </div>

      <!-- Summary Cards -->
      <div class="pm-grid" v-if="summary">
        <div class="pm-card"><div class="pm-card-label">Today</div><div class="pm-card-value" style="color:#10B981;">KSh {{ fmt(summary.today?.total) }}</div><div class="pm-card-sub">{{ summary.today?.cnt || 0 }} payments</div></div>
        <div class="pm-card"><div class="pm-card-label">This Week</div><div class="pm-card-value" style="color:#4FC3F7;">KSh {{ fmt(summary.week?.total) }}</div><div class="pm-card-sub">{{ summary.week?.cnt || 0 }} payments</div></div>
        <div class="pm-card"><div class="pm-card-label">This Month</div><div class="pm-card-value" style="color:#E8EDF4;">KSh {{ fmt(summary.month?.total) }}</div><div class="pm-card-sub">{{ summary.month?.cnt || 0 }} payments</div></div>
        <div class="pm-card" :style="{borderLeftColor: (summary.unmatched?.cnt > 0) ? '#EF4444' : '#10B981'}"><div class="pm-card-label">Unmatched</div><div class="pm-card-value" :style="{color: (summary.unmatched?.cnt > 0) ? '#EF4444' : '#10B981'}">{{ summary.unmatched?.cnt || 0 }}</div><div class="pm-card-sub">KSh {{ fmt(summary.unmatched?.total) }}</div></div>
      </div>

      <!-- Search Bar -->
      <div class="st-section mb-3" style="padding:14px;">
        <div class="d-flex gap-2 flex-wrap align-items-end">
          <div style="flex:1;min-width:200px;">
            <label class="st-label" style="font-size:10px;">M-Pesa Code (exact)</label>
            <input class="st-input" v-model="searchCode" @keyup.enter="searchPayment" placeholder="e.g. UD2CDBHQH2" />
          </div>
          <div v-if="paybills.length > 1" style="min-width:140px;">
            <label class="st-label" style="font-size:10px;">Paybill</label>
            <select class="st-input" v-model="selectedPaybillId" style="padding:9px 10px;"><option v-for="pb in paybills" :key="pb.id" :value="pb.id">{{ pb.shortcode }}</option></select>
          </div>
          <button class="btn st-btn-primary btn-sm" @click="searchPayment" :disabled="searching"><span v-if="searching" class="spinner-border spinner-border-sm me-1"></span>🔍</button>
          <button class="btn st-btn-outline btn-sm" @click="verifyWithSafaricom" :disabled="!searchCode || verifying"><span v-if="verifying" class="spinner-border spinner-border-sm me-1"></span>📡 Verify</button>
        </div>

        <!-- Search Result -->
        <div v-if="searchResult !== null" style="margin-top:12px;">
          <div v-if="searchResult" class="st-info-box" style="border-left:3px solid #10B981;">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <span style="font-size:14px;font-weight:700;color:#10B981;">✅ Found</span>
              <span class="pm-badge" :class="searchResult.reversed_at ? 'pm-badge-reversed' : searchResult.processed == 1 ? 'pm-badge-ok' : 'pm-badge-warn'">{{ searchResult.reversed_at ? 'Reversed' : (searchResult.processed == 1 ? 'Matched' : 'Unmatched') }}</span>
            </div>
            <div class="pm-details"><span>Receipt: <strong>{{ searchResult.provider_ref }}</strong></span><span>KSh <strong style="color:#10B981;">{{ fmt(searchResult.amount) }}</strong></span><span>{{ searchResult.payer_phone }}</span><span>Acc: {{ searchResult.provider_account }}</span><span v-if="searchResult.student_name" style="color:#4FC3F7;">{{ searchResult.student_name }}</span></div>
            <div v-if="searchResult.processed == 0 && !searchResult.reversed_at" class="d-flex gap-2 mt-2">
              <button class="btn st-btn-primary btn-sm" style="padding:4px 12px;font-size:12px;" @click="startMatching(searchResult)">🔗 Match</button>
              <button class="btn st-btn-danger btn-sm" style="padding:4px 12px;font-size:12px;" @click="startReversal(searchResult)">↩️ Reverse</button>
            </div>
            <div v-else-if="searchResult.processed == 1 && !searchResult.reversed_at" class="d-flex gap-2 mt-2 flex-wrap">
              <button class="btn st-btn-outline btn-sm" style="padding:4px 12px;font-size:12px;" @click="deallocatePayment(searchResult)" :disabled="deallocating"><span v-if="deallocating" class="spinner-border spinner-border-sm me-1"></span>🔓 Deallocate</button>
              <button class="btn btn-sm" style="padding:4px 12px;font-size:12px;background:rgba(239,68,68,.12);color:#EF4444;border:1px solid rgba(239,68,68,.4);border-radius:6px;" @click="startReversal(searchResult)">↩️ Reverse</button>
              <button v-if="searchResult.student_id" class="btn btn-sm" style="padding:4px 12px;font-size:12px;background:rgba(245,158,11,.12);color:#F59E0B;border:1px solid rgba(245,158,11,.4);border-radius:6px;" @click="startAdjustment(searchResult)">⚖️ Adjust Wallet</button>
            </div>
            <div v-else-if="searchResult.reversed_at" style="margin-top:6px;font-size:11px;color:#EF4444;">⛔ Reversed {{ searchResult.reversed_at.substring(0,16) }}</div>
          </div>
          <div v-else class="st-alert st-alert-danger">❌ Not found. <button class="btn st-btn-outline btn-sm ms-2" style="padding:2px 10px;font-size:11px;" @click="verifyWithSafaricom">📡 Verify</button></div>
        </div>
        <div v-if="verifyResult" class="st-alert mt-2" :class="verifyOk ? 'st-alert-success' : 'st-alert-danger'">{{ verifyResult }}</div>
      </div>

      <!-- Bulk Verify / Pull Section (collapsible) -->
      <div class="st-section mb-3" style="padding:14px;" v-if="showAdvanced">
        <div class="d-flex gap-3 flex-wrap">
          <!-- Bulk Verify -->
          <div style="flex:1;min-width:280px;">
            <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:8px;">📋 Bulk Verify</div>
            <p style="font-size:11px;color:#4A6A8A;margin-bottom:6px;">Paste multiple M-Pesa codes (max 50). One per line or comma-separated.</p>
            <textarea class="st-input mb-2" v-model="bulkCodes" rows="3" placeholder="UD2CDBHQH2&#10;UD2CDBI375&#10;UD2CDBHTL8" style="font-family:monospace;font-size:12px;"></textarea>
            <button class="btn st-btn-primary btn-sm" @click="bulkVerify" :disabled="bulkVerifying || !bulkCodes">
              <span v-if="bulkVerifying" class="spinner-border spinner-border-sm me-1"></span>📡 Verify All
            </button>
            <div v-if="bulkResults.length > 0" style="margin-top:8px;max-height:150px;overflow-y:auto;">
              <div v-for="r in bulkResults" :key="r.code" style="font-size:11px;padding:3px 0;display:flex;gap:8px;border-bottom:1px solid #1A3A6A;">
                <strong style="min-width:100px;color:#E8EDF4;">{{ r.code }}</strong>
                <span :style="{color: r.status === 'queued' ? '#4FC3F7' : r.status === 'matched' ? '#10B981' : r.status === 'error' ? '#EF4444' : '#F59E0B'}">{{ r.message }}</span>
              </div>
            </div>
          </div>
          <!-- Pull Transactions -->
          <div style="flex:1;min-width:280px;">
            <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:8px;">📥 Pull Transactions <span style="font-size:10px;color:#4A6A8A;">(last 48hrs)</span></div>
            <p style="font-size:11px;color:#4A6A8A;margin-bottom:6px;">Recover missed C2B callbacks. Step 1: Register, Step 2: Pull.</p>
            <div class="d-flex gap-2 mb-2 flex-wrap align-items-end">
              <div>
                <label class="st-label" style="font-size:10px;">Paybill</label>
                <select class="st-input" v-model="pullPaybillId" style="width:140px;padding:9px 8px;">
                  <option v-for="pb in paybills" :key="pb.id" :value="pb.id">{{ pb.shortcode }} — {{ pb.label }}</option>
                </select>
              </div>
              <div><label class="st-label" style="font-size:10px;">Phone (for notifications)</label><input class="st-input" v-model="pullPhone" placeholder="0704722837" style="width:140px;" /></div>
              <div><label class="st-label" style="font-size:10px;">From</label><input class="st-input" type="date" v-model="pullDateFrom" style="width:140px;" /></div>
              <div><label class="st-label" style="font-size:10px;">To</label><input class="st-input" type="date" v-model="pullDateTo" style="width:140px;" /></div>
            </div>
            <div class="d-flex gap-2">
              <button class="btn st-btn-outline btn-sm" @click="registerPullUrl" :disabled="registering">
                <span v-if="registering" class="spinner-border spinner-border-sm me-1"></span>1️⃣ Register
              </button>
              <button class="btn st-btn-primary btn-sm" @click="pullTransactions" :disabled="pulling">
                <span v-if="pulling" class="spinner-border spinner-border-sm me-1"></span>2️⃣ Pull
              </button>
            </div>
            <div v-if="pullResult" class="st-alert mt-2" :class="pullOk ? 'st-alert-success' : 'st-alert-danger'" style="font-size:12px;">{{ pullResult }}</div>
            <!-- Reconciliation Results -->
            <div v-if="pullTxList.length > 0" style="margin-top:10px;max-height:200px;overflow-y:auto;">
              <div style="font-size:11px;font-weight:600;color:#8BA4C4;margin-bottom:4px;">Safaricom Transactions:</div>
              <div v-for="tx in pullTxList" :key="tx.receipt" style="font-size:11px;padding:4px 0;display:flex;gap:8px;border-bottom:1px solid #1A3A6A;align-items:center;">
                <strong style="min-width:90px;color:#E8EDF4;">{{ tx.receipt }}</strong>
                <span style="min-width:70px;">KSh {{ Number(tx.amount).toLocaleString() }}</span>
                <span style="min-width:80px;color:#4A6A8A;">{{ tx.account || '—' }}</span>
                <span class="pm-badge" :class="{'pm-badge-ok': tx.status==='matched', 'pm-badge-warn': tx.status==='unmatched', 'pm-badge-reversed': tx.status==='reversed', 'pm-badge-info': tx.status==='recovered'}">{{ tx.status }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end mb-2">
        <button class="btn" style="font-size:11px;color:#4A6A8A;padding:2px 8px;" @click="showAdvanced = !showAdvanced">{{ showAdvanced ? '▲ Hide Advanced' : '▼ Bulk Verify / Pull Transactions' }}</button>
      </div>

      <!-- Filters -->
      <div class="st-section mb-3" style="padding:14px;">
        <div class="d-flex gap-2 flex-wrap align-items-end">
          <div><label class="st-label" style="font-size:10px;">From</label><input class="st-input" type="date" v-model="filters.dateFrom" style="width:140px;" /></div>
          <div><label class="st-label" style="font-size:10px;">To</label><input class="st-input" type="date" v-model="filters.dateTo" style="width:140px;" /></div>
          <div>
            <label class="st-label" style="font-size:10px;">Method</label>
            <select class="st-input" v-model="filters.method" style="width:130px;padding:9px 8px;">
              <option value="">All</option><option value="mpesa_stk">STK Push</option><option value="mpesa_c2b">C2B Paybill</option><option value="bank_transfer">Bank</option><option value="cash">Cash</option>
            </select>
          </div>
          <div>
            <label class="st-label" style="font-size:10px;">Purpose</label>
            <select class="st-input" v-model="filters.purpose" style="width:130px;padding:9px 8px;">
              <option value="">All</option><option value="wallet_topup">Wallet</option><option value="fee_payment">Fees</option><option value="sms_purchase">SMS</option><option value="pos_payment">POS</option>
            </select>
          </div>
          <div>
            <label class="st-label" style="font-size:10px;">Status</label>
            <select class="st-input" v-model="filters.status" style="width:120px;padding:9px 8px;">
              <option value="">All</option><option value="matched">Matched</option><option value="unmatched">Unmatched</option><option value="reversed">Reversed</option>
            </select>
          </div>
          <div><label class="st-label" style="font-size:10px;">Search</label><input class="st-input" v-model="filters.search" placeholder="Phone, name..." style="width:140px;" @keyup.enter="loadFilteredPayments" /></div>
          <button class="btn st-btn-primary btn-sm" @click="loadFilteredPayments" :disabled="loadingFiltered">🔍 Filter</button>
          <button class="btn st-btn-outline btn-sm" @click="resetFilters">✕</button>
        </div>
        <!-- Filter Summary -->
        <div v-if="filtered" class="d-flex gap-3 mt-2 flex-wrap" style="font-size:12px;color:#8BA4C4;">
          <span><strong style="color:#E8EDF4;">{{ filteredData.total_count }}</strong> payments</span>
          <span>Total: <strong style="color:#10B981;">KSh {{ fmt(filteredData.total_amount) }}</strong></span>
          <span v-for="m in filteredData.by_method" :key="m.payment_method">{{ m.payment_method }}: {{ m.cnt }} (KSh {{ fmt(m.total) }})</span>
        </div>
      </div>

      <!-- Payments Table -->
      <div class="st-section" style="padding:14px;overflow-x:auto;">
        <div v-if="loadingFiltered" class="st-spinner"><span class="spinner-border"></span></div>
        <div v-else-if="payments.length === 0" style="text-align:center;padding:30px;color:#4A6A8A;">No payments found</div>
        <table v-else class="pm-table">
          <thead>
          <tr><th>Date</th><th>Receipt</th><th>Amount</th><th>Payer</th><th>Account</th><th>Method</th><th>Purpose</th><th>Student</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
          <tr v-for="p in payments" :key="p.id" @click="viewDetail(p)" style="cursor:pointer;" :style="{opacity: p.reversed_at ? 0.5 : 1}">
            <td>{{ (p.completed_at || '').substring(0, 16) }}</td>
            <td><strong style="color:#4FC3F7;">{{ p.provider_ref || '—' }}</strong></td>
            <td style="font-weight:600;" :style="{color: p.reversed_at ? '#EF4444' : '#E8EDF4'}">{{ p.reversed_at ? '-' : '' }}KSh {{ fmt(p.amount) }}</td>
            <td>{{ p.payer_name || p.payer_phone || '—' }}</td>
            <td>{{ p.provider_account || '—' }}</td>
            <td><span class="pm-badge pm-badge-info">{{ methodLabel(p.payment_method) }}</span></td>
            <td>{{ purposeLabel(p.purpose) }}</td>
            <td>{{ p.student_name || '—' }}</td>
            <td><span class="pm-badge" :class="p.reversed_at ? 'pm-badge-reversed' : p.processed == 1 ? 'pm-badge-ok' : 'pm-badge-warn'">{{ p.reversed_at ? '↩️' : p.processed == 1 ? '✅' : '⚠️' }}</span></td>
            <td style="white-space:nowrap;">
              <button v-if="p.processed == 0 && !p.reversed_at" class="btn st-btn-primary btn-sm me-1" style="padding:2px 8px;font-size:10px;" @click.stop="startMatching(p)" title="Match to a student">🔗</button>
              <button v-if="p.processed == 1 && !p.reversed_at" class="btn st-btn-outline btn-sm me-1" style="padding:2px 8px;font-size:10px;" @click.stop="deallocatePayment(p)" title="Deallocate (clear student match)">🔓</button>
              <button v-if="!p.reversed_at" class="btn btn-sm me-1" style="padding:2px 8px;font-size:10px;background:rgba(239,68,68,.12);color:#EF4444;border:1px solid rgba(239,68,68,.4);border-radius:6px;" @click.stop="startReversal(p)" title="Reverse this payment (debit the wallet / fee balance)">↩️</button>
              <button v-if="p.student_id" class="btn btn-sm" style="padding:2px 8px;font-size:10px;background:rgba(245,158,11,.12);color:#F59E0B;border:1px solid rgba(245,158,11,.4);border-radius:6px;" @click.stop="startAdjustment(p)" title="Manual wallet adjustment (correction)">⚖️</button>
            </td>
          </tr>
          </tbody>
        </table>
        <!-- Pagination -->
        <div v-if="filteredData.total_count > filters.limit" class="d-flex justify-content-between align-items-center mt-3" style="font-size:12px;color:#8BA4C4;">
          <span>Showing {{ filters.offset + 1 }}–{{ Math.min(filters.offset + filters.limit, filteredData.total_count) }} of {{ filteredData.total_count }}</span>
          <div class="d-flex gap-2">
            <button class="btn st-btn-outline btn-sm" :disabled="filters.offset === 0" @click="filters.offset -= filters.limit; loadFilteredPayments()">← Prev</button>
            <button class="btn st-btn-outline btn-sm" :disabled="filters.offset + filters.limit >= filteredData.total_count" @click="filters.offset += filters.limit; loadFilteredPayments()">Next →</button>
          </div>
        </div>
      </div>

      <!-- Detail Modal -->
      <div v-if="detailPayment" class="pm-overlay" @click.self="detailPayment=null">
        <div class="pm-modal" style="max-width:600px;">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 style="color:#E8EDF4;margin:0;font-size:15px;">💳 Payment Detail</h5>
            <button class="btn" style="color:#8BA4C4;font-size:18px;padding:0;" @click="detailPayment=null">✕</button>
          </div>
          <div class="pm-detail-grid">
            <div class="pm-detail-row"><span class="pm-detail-label">Payment Ref</span><span>{{ detailPayment.payment_ref }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">M-Pesa Receipt</span><span style="color:#4FC3F7;font-weight:700;">{{ detailPayment.provider_ref || '—' }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">Amount</span><span style="font-size:18px;font-weight:700;color:#10B981;">KSh {{ fmt(detailPayment.amount) }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">Method</span><span>{{ methodLabel(detailPayment.payment_method) }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">Purpose</span><span>{{ purposeLabel(detailPayment.purpose) }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">Shortcode</span><span>{{ detailPayment.shortcode || '—' }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">Payer Name</span><span>{{ detailPayment.payer_name || '—' }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">Payer Phone</span><span>{{ detailPayment.payer_phone || '—' }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">Account Ref</span><span>{{ detailPayment.provider_account || '—' }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">Student</span><span style="color:#4FC3F7;">{{ detailPayment.student_name || '—' }} {{ detailPayment.admission_no ? '('+detailPayment.admission_no+')' : '' }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">Status</span>
              <span class="pm-badge" :class="detailPayment.reversed_at ? 'pm-badge-reversed' : detailPayment.processed == 1 ? 'pm-badge-ok' : 'pm-badge-warn'">{{ detailPayment.reversed_at ? 'Reversed' : detailPayment.processed == 1 ? 'Matched' : 'Unmatched' }}</span>
            </div>
            <div class="pm-detail-row"><span class="pm-detail-label">Completed</span><span>{{ detailPayment.completed_at }}</span></div>
            <div class="pm-detail-row"><span class="pm-detail-label">Created</span><span>{{ detailPayment.created_at }}</span></div>
            <div v-if="detailPayment.reversed_at" class="pm-detail-row"><span class="pm-detail-label">Reversed</span><span style="color:#EF4444;">{{ detailPayment.reversed_at }}</span></div>
            <div v-if="detailPayment.processing_notes" class="pm-detail-row"><span class="pm-detail-label">Notes</span><span style="font-size:11px;color:#F59E0B;">{{ detailPayment.processing_notes }}</span></div>
          </div>
          <div class="d-flex gap-2 mt-3 flex-wrap">
            <button v-if="detailPayment.processed == 0 && !detailPayment.reversed_at" class="btn st-btn-primary btn-sm" @click="startMatching(detailPayment); detailPayment=null;">🔗 Match</button>
            <button v-if="!detailPayment.reversed_at" class="btn btn-sm" style="background:rgba(239,68,68,.12);color:#EF4444;border:1px solid rgba(239,68,68,.4);" @click="startReversal(detailPayment); detailPayment=null;">↩️ Reverse</button>
            <button v-if="detailPayment.processed == 1 && !detailPayment.reversed_at" class="btn st-btn-outline btn-sm" @click="deallocatePayment(detailPayment); detailPayment=null;">🔓 Deallocate</button>
            <button v-if="detailPayment.student_id" class="btn btn-sm" style="background:rgba(245,158,11,.12);color:#F59E0B;border:1px solid rgba(245,158,11,.4);" @click="startAdjustment(detailPayment); detailPayment=null;">⚖️ Adjust Wallet</button>
          </div>
        </div>
      </div>

      <!-- Match Modal -->
      <div v-if="matchingPayment" class="pm-overlay" @click.self="matchingPayment=null">
        <div class="pm-modal">
          <h5 style="color:#E8EDF4;margin:0 0 16px;font-size:15px;">🔗 Match Payment</h5>
          <div class="st-info-box mb-3">
            <span style="font-size:13px;color:#8BA4C4;">{{ matchingPayment.provider_ref }} — KSh <strong style="color:#10B981;">{{ fmt(matchingPayment.amount) }}</strong></span>
          </div>
          <label class="st-label">Search Student</label>
          <input class="st-input mb-2" v-model="studentSearch" @input="searchStudents" placeholder="Name or admission number..." />
          <div v-if="studentResults.length > 0" style="max-height:180px;overflow-y:auto;margin-bottom:10px;">
            <div v-for="s in studentResults" :key="s.id" @click="selectedStudent = s" class="pm-student" :class="{selected: selectedStudent?.id === s.id}">
              <strong>{{ s.name }}</strong> <span style="color:#4FC3F7;margin-left:6px;">{{ s.admission_no }}</span>
              <span v-if="s.class_name" style="color:#4A6A8A;margin-left:6px;font-size:11px;">{{ s.class_name }}</span>
            </div>
          </div>
          <div v-if="selectedStudent" class="d-flex gap-2 mb-3">
            <button class="btn btn-sm" :class="matchType === 'fee_payment' ? 'st-btn-primary' : 'st-btn-outline'" style="padding:5px 12px;font-size:12px;" @click="matchType='fee_payment'">🎓 Fees</button>
            <button class="btn btn-sm" :class="matchType === 'wallet_topup' ? 'st-btn-primary' : 'st-btn-outline'" style="padding:5px 12px;font-size:12px;" @click="matchType='wallet_topup'">💰 Wallet</button>
          </div>
          <div class="d-flex gap-2 justify-content-end">
            <button class="btn st-btn-outline btn-sm" @click="matchingPayment=null">Cancel</button>
            <button class="btn st-btn-primary btn-sm" :disabled="!selectedStudent || matching" @click="confirmMatch"><span v-if="matching" class="spinner-border spinner-border-sm me-1"></span>Confirm</button>
          </div>
          <div v-if="matchResult" class="st-alert mt-2" :class="matchOk ? 'st-alert-success' : 'st-alert-danger'">{{ matchResult }}</div>
        </div>
      </div>

      <!-- Reversal Modal -->
      <div v-if="reversingPayment" class="pm-overlay" @click.self="reversingPayment=null">
        <div class="pm-modal">
          <h5 style="color:#EF4444;margin:0 0 16px;font-size:15px;">↩️ Reverse Payment</h5>
          <div class="st-info-box mb-3"><span style="font-size:13px;color:#8BA4C4;">{{ reversingPayment.provider_ref }} — KSh <strong style="color:#EF4444;">{{ fmt(reversingPayment.amount) }}</strong></span></div>
          <label class="st-label">Reason</label>
          <input class="st-input mb-3" v-model="reversalReason" placeholder="e.g. Wrong payment, duplicate..." />
          <p v-if="reversingPayment.processed == 1" style="font-size:12px;color:#F59E0B;margin-bottom:12px;">⚠️ This will <strong>debit</strong> {{ reversingPayment.purpose === 'wallet_topup' ? "the student's wallet" : "the fee balance" }} by KSh {{ fmt(reversingPayment.amount) }}. Refuses if wallet has been spent down already.</p>
          <p v-else style="font-size:12px;color:#F59E0B;margin-bottom:12px;">⚠️ Marks the payment reversed. Use when the money was returned to the customer's M-Pesa.</p>
          <div class="d-flex gap-2 justify-content-end">
            <button class="btn st-btn-outline btn-sm" @click="reversingPayment=null">Cancel</button>
            <button class="btn st-btn-danger btn-sm" :disabled="reversing" @click="confirmReversal"><span v-if="reversing" class="spinner-border spinner-border-sm me-1"></span>Confirm Reversal</button>
          </div>
          <div v-if="reversalResult" class="st-alert mt-2" :class="reversalOk ? 'st-alert-success' : 'st-alert-danger'">{{ reversalResult }}</div>
        </div>
      </div>

      <!-- Wallet Adjustment Modal -->
      <div v-if="adjustingPayment" class="pm-overlay" @click.self="adjustingPayment=null">
        <div class="pm-modal" style="max-width:520px;">
          <h5 style="color:#F59E0B;margin:0 0 12px;font-size:15px;">⚖️ Manual Wallet Adjustment</h5>
          <div class="st-info-box mb-3" style="background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;">
            <div style="font-size:12px;color:#8BA4C4;">Student</div>
            <div style="color:#E8EDF4;font-weight:700;">{{ adjustingPayment.student_name || ('ID ' + adjustingPayment.student_id) }}</div>
            <div style="font-size:11px;color:#8BA4C4;margin-top:4px;">Reference payment: {{ adjustingPayment.provider_ref }} (KSh {{ fmt(adjustingPayment.amount) }})</div>
          </div>

          <div style="font-size:12px;color:#8BA4C4;background:rgba(245,158,11,.06);border:1px solid rgba(245,158,11,.2);border-radius:8px;padding:8px 12px;margin-bottom:12px;">
            <i class="mdi mdi-information"></i> The original payment row is <strong>not</strong> changed. This writes a <code>wallet_transactions</code> row tagged ADJUSTMENT so it appears clearly in statements.
          </div>

          <div class="d-flex gap-2 mb-3">
            <button class="btn flex-fill" :class="adjustForm.direction === 'debit' ? 'st-btn-danger' : 'st-btn-outline'" style="font-size:12px;padding:6px 12px;" @click="adjustForm.direction = 'debit'">➖ Debit (remove from wallet)</button>
            <button class="btn flex-fill" :class="adjustForm.direction === 'credit' ? 'st-btn-success' : 'st-btn-outline'" style="font-size:12px;padding:6px 12px;" @click="adjustForm.direction = 'credit'">➕ Credit (add to wallet)</button>
          </div>

          <label class="st-label">Amount (KSh) *</label>
          <input type="number" min="0" step="0.01" class="st-input mb-3" v-model.number="adjustForm.amount" placeholder="2500" />

          <label class="st-label">Reason *</label>
          <input class="st-input mb-3" v-model="adjustForm.reason" placeholder="e.g. Duplicate match cleanup — credited twice due to retry" />

          <label class="st-label">Reference <small style="color:#8BA4C4;font-weight:400;">(M-Pesa code or correction note, optional)</small></label>
          <input class="st-input mb-3" v-model="adjustForm.reference" :placeholder="adjustingPayment.provider_ref" />

          <div class="d-flex gap-2 justify-content-end">
            <button class="btn st-btn-outline btn-sm" @click="adjustingPayment=null">Cancel</button>
            <button class="btn btn-sm" style="background:rgba(245,158,11,.2);color:#F59E0B;border:1px solid rgba(245,158,11,.5);" :disabled="adjusting || !adjustForm.amount || !adjustForm.reason" @click="confirmAdjustment">
              <span v-if="adjusting" class="spinner-border spinner-border-sm me-1"></span>Apply Adjustment
            </button>
          </div>
          <div v-if="adjustResult" class="st-alert mt-2" :class="adjustOk ? 'st-alert-success' : 'st-alert-danger'">{{ adjustResult }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: 'PaymentManagement',
  components: { Navigation },
  data() {
    return {
      summary: null,
      searchCode: '', searching: false, searchResult: null,
      verifying: false, verifyResult: '', verifyOk: false,
      // Filters
      filters: { dateFrom: '', dateTo: '', method: '', purpose: '', status: '', search: '', limit: 50, offset: 0 },
      filtered: false, filteredData: { total_count: 0, total_amount: 0, by_method: [], by_purpose: [] },
      payments: [], loadingFiltered: false,
      // Detail
      detailPayment: null,
      // Match
      matchingPayment: null, studentSearch: '', studentResults: [], selectedStudent: null,
      matchType: 'fee_payment', matching: false, matchResult: '', matchOk: false,
      // Reversal
      reversingPayment: null, reversalReason: '', reversing: false, reversalResult: '', reversalOk: false,
      adjustingPayment: null, adjustForm: { direction: 'debit', amount: null, reason: '', reference: '' }, adjusting: false, adjustResult: '', adjustOk: false,
      deallocating: false,
      // Balance
      balanceLoading: false, balanceResult: '', balanceOk: false, lastBalance: null, showBalance: false,
      // Paybills
      paybills: [], selectedPaybillId: '',
      exporting: false, searchTimer: null,
      // Advanced
      showAdvanced: false,
      bulkCodes: '', bulkVerifying: false, bulkResults: [],
      pullDateFrom: '', pullDateTo: '', pullPhone: '', pullPaybillId: '', pulling: false, pullResult: '', pullOk: false, registering: false, pullTxList: [],
    };
  },
  mounted() { this.loadAll(); },
  methods: {
    fmt(n) { return Number(n || 0).toLocaleString(); },
    methodLabel(m) { return { mpesa_stk: 'STK', mpesa_c2b: 'C2B', bank_transfer: 'Bank', card: 'Card', cash: 'Cash', cheque: 'Cheque' }[m] || m; },
    purposeLabel(p) { return { wallet_topup: 'Wallet', fee_payment: 'Fees', sms_purchase: 'SMS', pos_payment: 'POS', subscription: 'Sub', donation: 'Donation' }[p] || p; },

    async loadAll() {
      this.loadSummary(); this.loadFilteredPayments(); this.loadPaybills();
    },
    async loadPaybills() {
      try {
        const res = await executeJson({ function: 'getSettings' }, simitrack);
        if (res.data.success) {
          this.paybills = res.data.data?.paybills || [];
          if (this.paybills.length > 0 && !this.selectedPaybillId) {
            const primary = this.paybills.find(p => p.isPrimary);
            this.selectedPaybillId = primary ? primary.id : this.paybills[0].id;
            if (!this.pullPaybillId) this.pullPaybillId = this.selectedPaybillId;
          }
        }
      } catch (e) {}
    },
    async loadSummary() {
      try {
        const res = await executeJson({ function: 'getPaymentSummary' }, simitrack);
        if (res.data.success) this.summary = res.data.data;
      } catch (e) {}
    },
    async loadFilteredPayments() {
      this.loadingFiltered = true;
      try {
        const res = await executeJson({ function: 'getFilteredPayments', ...this.filters }, simitrack);
        if (res.data.success) {
          this.payments = res.data.data?.payments || [];
          this.filteredData = { total_count: res.data.data?.total_count || 0, total_amount: res.data.data?.total_amount || 0, by_method: res.data.data?.by_method || [], by_purpose: res.data.data?.by_purpose || [] };
          this.filtered = !!(this.filters.dateFrom || this.filters.dateTo || this.filters.method || this.filters.purpose || this.filters.status || this.filters.search);
        }
      } catch (e) {}
      this.loadingFiltered = false;
    },
    resetFilters() {
      this.filters = { dateFrom: '', dateTo: '', method: '', purpose: '', status: '', search: '', limit: 50, offset: 0 };
      this.filtered = false;
      this.loadFilteredPayments();
    },

    async searchPayment() {
      if (!this.searchCode || this.searchCode.length < 8) return;
      this.searching = true; this.searchResult = null;
      try {
        const res = await executeJson({ function: 'searchPaymentByCode', code: this.searchCode.trim() }, simitrack);
        this.searchResult = res.data.success ? res.data.data : false;
      } catch (e) { this.searchResult = false; }
      this.searching = false;
    },
    async verifyWithSafaricom() {
      if (!this.searchCode) return;
      this.verifying = true; this.verifyResult = '';
      try {
        const res = await executeJson({ function: 'verifyWithSafaricom', code: this.searchCode.trim(), paybillId: this.selectedPaybillId }, simitrack);
        this.verifyResult = res.data.message; this.verifyOk = res.data.success;
        if (res.data.success) {
          this.verifyResult = 'Waiting for Safaricom...';
          let attempts = 0;
          const poll = setInterval(async () => {
            attempts++;
            try {
              const r = await executeJson({ function: 'searchPaymentByCode', code: this.searchCode.trim() }, simitrack);
              if (r.data.success && r.data.data) {
                this.searchResult = r.data.data;
                this.verifyResult = '✅ Payment verified!';
                this.verifyOk = true;
                clearInterval(poll); this.loadAll();
              } else if (attempts >= 10) {
                this.verifyResult = 'Request sent. Try searching again in a minute.';
                clearInterval(poll);
              } else { this.verifyResult = 'Waiting... (' + (attempts * 3) + 's)'; }
            } catch(e) {}
          }, 3000);
        }
      } catch (e) { this.verifyResult = 'Connection error'; }
      this.verifying = false;
    },

    viewDetail(p) { this.detailPayment = p; },

    // Match
    startMatching(p) {
      this.matchingPayment = p; this.studentSearch = p.provider_account || '';
      this.studentResults = []; this.selectedStudent = null; this.matchType = 'fee_payment'; this.matchResult = '';
      if (this.studentSearch) this.searchStudents();
    },
    searchStudents() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(async () => {
        if (this.studentSearch.length < 2) { this.studentResults = []; return; }
        try {
          const res = await executeJson({ function: 'searchStudentsForMatch', query: this.studentSearch }, simitrack);
          if (res.data.success) this.studentResults = res.data.data || [];
        } catch (e) { this.studentResults = []; }
      }, 300);
    },
    async confirmMatch() {
      if (!this.selectedStudent || !this.matchingPayment) return;
      this.matching = true; this.matchResult = '';
      try {
        const res = await executeJson({ function: 'matchPaymentToStudent', paymentId: this.matchingPayment.id, studentId: this.selectedStudent.id, type: this.matchType }, simitrack);
        this.matchResult = res.data.message; this.matchOk = res.data.success;
        if (res.data.success) setTimeout(() => { this.matchingPayment = null; this.loadAll(); if (this.searchCode) this.searchPayment(); }, 1200);
      } catch (e) { this.matchResult = 'Error'; }
      this.matching = false;
    },

    // Reversal
    startReversal(p) { this.reversingPayment = p; this.reversalReason = ''; this.reversalResult = ''; },
    async confirmReversal() {
      if (!this.reversingPayment) return;
      this.reversing = true; this.reversalResult = '';
      try {
        const res = await executeJson({ function: 'reversePayment', paymentId: this.reversingPayment.id, reason: this.reversalReason || 'Admin reversal' }, simitrack);
        this.reversalResult = res.data.message; this.reversalOk = res.data.success;
        if (res.data.success) setTimeout(() => { this.reversingPayment = null; this.loadAll(); if (this.searchCode) this.searchPayment(); }, 1500);
      } catch (e) { this.reversalResult = 'Error'; }
      this.reversing = false;
    },

    // Wallet adjustment
    startAdjustment(p) {
      this.adjustingPayment = p;
      this.adjustForm = {
        direction: 'debit',
        amount: p && p.amount ? Number(p.amount) : null,
        reason: '',
        reference: p && p.provider_ref ? p.provider_ref : '',
      };
      this.adjustResult = '';
      this.adjustOk = false;
    },
    async confirmAdjustment() {
      if (!this.adjustingPayment) return;
      if (!this.adjustForm.amount || this.adjustForm.amount <= 0) { this.adjustResult = 'Amount must be greater than 0'; this.adjustOk = false; return; }
      if (!this.adjustForm.reason) { this.adjustResult = 'Reason is required'; this.adjustOk = false; return; }
      const verb = this.adjustForm.direction === 'debit' ? 'remove from' : 'add to';
      if (!confirm(`${verb.toUpperCase()} the wallet: KSh ${this.adjustForm.amount}?\n\nReason: ${this.adjustForm.reason}\n\nThis is irreversible without another adjustment.`)) return;
      this.adjusting = true; this.adjustResult = '';
      try {
        const res = await executeJson({
          function: 'adjustWallet',
          studentId: this.adjustingPayment.student_id,
          direction: this.adjustForm.direction,
          amount: this.adjustForm.amount,
          reason: this.adjustForm.reason,
          reference: this.adjustForm.reference || '',
        }, simitrack);
        this.adjustResult = res.data.message; this.adjustOk = res.data.success;
        if (res.data.success) setTimeout(() => { this.adjustingPayment = null; this.loadAll(); if (this.searchCode) this.searchPayment(); }, 1800);
      } catch (e) { this.adjustResult = 'Connection error'; this.adjustOk = false; }
      this.adjusting = false;
    },

    // Deallocate
    async deallocatePayment(p) {
      if (!confirm('Deallocate? Wallet/fee effects will be undone.')) return;
      this.deallocating = true;
      try {
        const res = await executeJson({ function: 'deallocatePayment', paymentId: p.id }, simitrack);
        if (res.data.success) { alert(res.data.message); this.searchResult = null; this.detailPayment = null; this.loadAll(); if (this.searchCode) this.searchPayment(); }
        else alert(res.data.message);
      } catch (e) { alert('Error'); }
      this.deallocating = false;
    },

    // Balance
    async checkBalance() {
      this.balanceLoading = true; this.balanceResult = '';
      try {
        const res = await executeJson({ function: 'checkAccountBalance', paybillId: this.selectedPaybillId }, simitrack);
        if (res.data.success) {
          this.balanceResult = 'Requesting balance from Safaricom...';
          this.balanceOk = true;
          // Poll for result
          let attempts = 0;
          const poll = setInterval(async () => {
            attempts++;
            try {
              const r = await executeJson({ function: 'getLastBalance' }, simitrack);
              if (r.data.success && r.data.data?.timestamp) {
                const ts = new Date(r.data.data.timestamp).getTime();
                const now = Date.now();
                if (now - ts < 60000) { // Result arrived in last 60 seconds
                  this.balanceResult = r.data.message;
                  this.balanceOk = true;
                  this.lastBalance = r.data.data;
                  clearInterval(poll);
                }
              }
              if (attempts >= 10) {
                this.balanceResult = 'Balance request sent. Check back in a moment.';
                clearInterval(poll);
              }
            } catch(e) {}
          }, 3000);
        } else {
          this.balanceResult = res.data.message; this.balanceOk = false;
        }
      } catch (e) { this.balanceResult = 'Error'; }
      this.balanceLoading = false;
    },
    async loadLastBalance() {
      try {
        const res = await executeJson({ function: 'getLastBalance' }, simitrack);
        if (res.data.success) {
          this.lastBalance = res.data.data;
          this.balanceResult = res.data.message;
          this.balanceOk = true;
        }
      } catch(e) {}
    },

    // Export
    async exportCsv() {
      this.exporting = true;
      try {
        const res = await executeJson({ function: 'exportPayments', ...this.filters }, simitrack);
        if (res.data.success) {
          const d = res.data.data;
          const inst = d.institution || 'Institution';
          let csv = `"${inst} — Payment Report"\n`;
          csv += `"Period: ${d.date_from || 'All'} to ${d.date_to || 'All'}"\n`;
          csv += `"Total Payments: ${d.total_count} | Total Amount: KSh ${Number(d.total_amount).toLocaleString()}"\n`;
          csv += `"Generated: ${new Date().toLocaleString()}"\n\n`;
          csv += 'Date,M-Pesa Receipt,Amount (KSh),Payer Name,Phone,Account Ref,Purpose,Method,Shortcode,Student Name,Admission No,Status,Notes\n';
          (d.payments || []).forEach(p => {
            csv += `"${p.completed_at}","${p.mpesa_code}","${p.amount}","${(p.payer_name||'').replace(/"/g,'""')}","${p.payer_phone}","${p.account_ref}","${p.purpose}","${p.payment_method}","${p.shortcode}","${p.student_name || ''}","${p.admission_no || ''}","${p.status}","${(p.processing_notes || '').replace(/"/g, '""')}"\n`;
          });
          const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `${inst.replace(/\s+/g,'_')}_Payments_${d.date_from || 'all'}_to_${d.date_to || 'all'}.csv`; a.click();
          URL.revokeObjectURL(url);
        }
      } catch (e) { alert('Export failed'); }
      this.exporting = false;
    },
    async exportPdf() {
      this.exporting = true;
      try {
        const res = await executeJson({ function: 'exportPayments', ...this.filters }, simitrack);
        if (res.data.success) {
          const d = res.data.data;
          const inst = d.institution || 'Institution';
          const now = new Date().toLocaleString();
          const statusFilter = this.filters.status ? ` | Status: ${this.filters.status}` : '';
          const methodFilter = this.filters.method ? ` | Method: ${this.filters.method}` : '';

          // Count by status
          let matched = 0, unmatched = 0, reversed = 0;
          (d.payments||[]).forEach(p => { if(p.status==='Matched') matched++; else if(p.status==='Reversed') reversed++; else unmatched++; });

          let html = `<!DOCTYPE html><html><head><title>${inst} - Payment Report</title>
          <style>
            @page { size: A4 landscape; margin: 12mm; }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; font-size: 11px; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #2E86DE; padding-bottom: 12px; margin-bottom: 14px; }
            .header h1 { font-size: 20px; color: #0D2B5E; margin-bottom: 2px; }
            .header .subtitle { font-size: 13px; color: #2E86DE; font-weight: 600; }
            .header .meta { text-align: right; font-size: 10px; color: #666; }
            .summary-row { display: flex; gap: 12px; margin-bottom: 14px; }
            .summary-box { flex: 1; background: #f0f7ff; border: 1px solid #d0e3f7; border-radius: 6px; padding: 10px 14px; text-align: center; }
            .summary-box .label { font-size: 9px; color: #666; text-transform: uppercase; font-weight: 600; }
            .summary-box .value { font-size: 18px; font-weight: 800; color: #0D2B5E; }
            .summary-box .sub { font-size: 10px; color: #888; }
            table { width: 100%; border-collapse: collapse; margin-top: 4px; }
            th { background: #0D2B5E; color: #fff; padding: 6px 8px; text-align: left; font-size: 9px; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; }
            td { padding: 5px 8px; border-bottom: 1px solid #e0e0e0; font-size: 10px; }
            tr:nth-child(even) { background: #f9fbfd; }
            tr.reversed { background: #fff0f0; color: #999; text-decoration: line-through; }
            .status { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 9px; font-weight: 600; }
            .status-matched { background: #d1fae5; color: #065f46; }
            .status-unmatched { background: #fef3c7; color: #92400e; }
            .status-reversed { background: #fee2e2; color: #991b1b; }
            .amount { text-align: right; font-weight: 600; }
            .footer { margin-top: 16px; padding-top: 10px; border-top: 1px solid #ddd; display: flex; justify-content: space-between; font-size: 9px; color: #999; }
          </style></head><body>`;

          html += `<div class="header">
            <div>
              <h1>${inst}</h1>
              <div class="subtitle">Payment Report</div>
              <div style="font-size:11px;color:#444;margin-top:4px;">Period: ${d.date_from || 'All dates'} to ${d.date_to || 'All dates'}${statusFilter}${methodFilter}</div>
            </div>
            <div class="meta">
              Generated: ${now}<br/>
              Simi Trac Payment System
            </div>
          </div>`;

          html += `<div class="summary-row">
            <div class="summary-box"><div class="label">Total Payments</div><div class="value">${d.total_count}</div></div>
            <div class="summary-box"><div class="label">Total Amount</div><div class="value">KSh ${Number(d.total_amount).toLocaleString()}</div></div>
            <div class="summary-box"><div class="label">Matched</div><div class="value" style="color:#065f46;">${matched}</div></div>
            <div class="summary-box"><div class="label">Unmatched</div><div class="value" style="color:#92400e;">${unmatched}</div></div>
            <div class="summary-box"><div class="label">Reversed</div><div class="value" style="color:#991b1b;">${reversed}</div></div>
          </div>`;

          html += `<table><thead><tr>
            <th>Date</th><th>M-Pesa Receipt</th><th class="amount">Amount</th><th>Payer</th><th>Phone</th><th>Account Ref</th><th>Purpose</th><th>Student</th><th>Admission</th><th>Status</th>
          </tr></thead><tbody>`;

          (d.payments || []).forEach(p => {
            const cls = p.status === 'Reversed' ? ' class="reversed"' : '';
            const statusCls = p.status === 'Matched' ? 'status-matched' : p.status === 'Reversed' ? 'status-reversed' : 'status-unmatched';
            html += `<tr${cls}>
              <td>${(p.completed_at||'').substring(0,16)}</td>
              <td><strong>${p.mpesa_code||''}</strong></td>
              <td class="amount">KSh ${Number(p.amount).toLocaleString()}</td>
              <td>${p.payer_name||''}</td>
              <td>${p.payer_phone||''}</td>
              <td>${p.account_ref||''}</td>
              <td>${p.purpose||''}</td>
              <td>${p.student_name||''}</td>
              <td>${p.admission_no||''}</td>
              <td><span class="status ${statusCls}">${p.status}</span></td>
            </tr>`;
          });

          html += `</tbody></table>`;
          html += `<div class="footer">
            <span>${inst} — Simi Trac Payment Report</span>
            <span>Page 1 | ${d.total_count} records | Generated ${now}</span>
          </div>`;
          html += `</body></html>`;

          const w = window.open('', '_blank');
          w.document.write(html); w.document.close();
          setTimeout(() => w.print(), 600);
        }
      } catch (e) { alert('Export failed'); }
      this.exporting = false;
    },

    // Bulk verify
    async bulkVerify() {
      if (!this.bulkCodes) return;
      this.bulkVerifying = true; this.bulkResults = [];
      try {
        const res = await executeJson({ function: 'bulkVerifyPayments', codes: this.bulkCodes, paybillId: this.selectedPaybillId }, simitrack);
        if (res.data.success) {
          this.bulkResults = res.data.data?.results || [];
          // Auto-refresh after 10 seconds to pick up verified payments
          setTimeout(() => this.loadAll(), 10000);
        }
      } catch (e) { alert('Bulk verify failed'); }
      this.bulkVerifying = false;
    },

    // Pull transactions
    async registerPullUrl() {
      this.registering = true; this.pullResult = '';
      try {
        const res = await executeJson({ function: 'registerPullUrl', paybillId: this.pullPaybillId, nominatedNumber: this.pullPhone }, simitrack);
        this.pullResult = res.data.message; this.pullOk = res.data.success;
      } catch (e) { this.pullResult = 'Connection error'; }
      this.registering = false;
    },
    async pullTransactions() {
      if (!this.pullDateFrom || !this.pullDateTo) { alert('Select date range'); return; }
      this.pulling = true; this.pullResult = ''; this.pullTxList = [];
      try {
        const res = await executeJson({ function: 'pullTransactions', startDate: this.pullDateFrom, endDate: this.pullDateTo, paybillId: this.pullPaybillId }, simitrack);
        this.pullResult = res.data.message; this.pullOk = res.data.success;
        if (res.data.success) {
          this.pullTxList = res.data.data?.transactions || [];
          this.loadAll();
        }
      } catch (e) { this.pullResult = 'Connection error'; }
      this.pulling = false;
    },
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-section{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:20px;}
.st-label{color:#8BA4C4;font-size:12px;font-weight:600;margin-bottom:4px;display:block;}
.st-input{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;color:#E8EDF4;font-size:13px;outline:none;}.st-input:focus{border-color:#2E86DE;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}
.st-btn-primary:disabled{opacity:.6;}
.st-btn-danger{background:rgba(239,68,68,.15);border:1px solid #EF4444;color:#EF4444;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}
.st-btn-outline{background:transparent;border:1px solid #1A3A6A;color:#8BA4C4;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}.st-btn-outline:hover{border-color:#4FC3F7;color:#4FC3F7;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;margin-bottom:12px;}
.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#6EE7B7;}
.st-alert-danger{background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.2);color:#EF4444;}
.st-info-box{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px;}
.st-spinner{display:flex;justify-content:center;padding:30px;}.st-spinner .spinner-border{color:#2E86DE;}

.pm-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:10px;margin-bottom:16px;}
.pm-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:10px;padding:14px;border-left:3px solid #1A3A6A;}
.pm-card-label{font-size:10px;color:#4A6A8A;text-transform:uppercase;font-weight:600;letter-spacing:.5px;}
.pm-card-value{font-size:20px;font-weight:800;margin:2px 0;}
.pm-card-sub{font-size:11px;color:#8BA4C4;}
.pm-details{display:flex;gap:12px;flex-wrap:wrap;font-size:12px;color:#8BA4C4;}
.pm-badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:600;}
.pm-badge-ok{background:rgba(16,185,129,.15);color:#10B981;}
.pm-badge-warn{background:rgba(245,158,11,.15);color:#F59E0B;}
.pm-badge-reversed{background:rgba(239,68,68,.15);color:#EF4444;}
.pm-badge-info{background:rgba(79,195,247,.15);color:#4FC3F7;}

.pm-table{width:100%;border-collapse:collapse;font-size:12px;color:#E8EDF4;}
.pm-table thead tr{background:#0A1628;}
.pm-table th{padding:8px;color:#4FC3F7;font-weight:600;text-align:left;font-size:10px;text-transform:uppercase;border-bottom:2px solid #1A3A6A;white-space:nowrap;}
.pm-table td{padding:7px 8px;border-bottom:1px solid #1A3A6A;white-space:nowrap;}
.pm-table tr:hover{background:rgba(46,134,222,.05);}

.pm-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;z-index:999;}
.pm-modal{background:#0D2B5E;border-radius:14px;padding:24px;width:92%;max-width:480px;max-height:85vh;overflow-y:auto;border:1px solid #1A3A6A;}
.pm-student{padding:8px 12px;border-radius:6px;cursor:pointer;margin-bottom:3px;background:#0A1628;border:1px solid #1A3A6A;font-size:13px;}.pm-student:hover{border-color:#4FC3F7;}.pm-student.selected{border-color:#10B981;background:rgba(16,185,129,.08);}
.pm-detail-grid{display:grid;gap:0;}
.pm-detail-row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #1A3A6A;font-size:12px;}
.pm-detail-label{color:#4A6A8A;font-weight:600;min-width:120px;}

@media(max-width:768px){.pm-grid{grid-template-columns:repeat(2,1fr);}.pm-card-value{font-size:16px;}}
</style>